"use client";

import type React from "react";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";

const Signup = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";
  const inputBg = isDark ? "gray.600" : "gray.200";
  const linkColor = isDark ? "gray.300" : "gray.500";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const dashboardIconUrl = isDark
    ? process.env.REACT_APP_DASHBOARD_DARK_ICON_URL || ""
    : process.env.REACT_APP_DASHBOARD_LIGHT_ICON_URL || "";

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        },
      });

      if (error) throw error;

      // Create profile for the user
      if (data.user) {
        try {
          // Call our server endpoint to ensure profile is created
          const response = await fetch("/api/auth/create-profile", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "user-id": data.user.id,
            },
            body: JSON.stringify({ userId: data.user.id }),
          });

          if (!response.ok) {
            console.warn(
              "Failed to create profile, but continuing with signup flow"
            );
          }
        } catch (profileError) {
          console.error("Error creating profile:", profileError);
          // Continue with signup flow even if profile creation fails
        }
      }

      // Redirect to OTP verification page
      navigate("/otp-verification", {
        state: {
          email,
        },
      });
    } catch (error: any) {
      toast({
        title: "Error signing up",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <img
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              style={{ maxHeight: "60px", marginBottom: "1rem" }}
            />
          )}
          <Heading color={headingColor}>Create an account</Heading>
          <Text color={textColor}>Sign up to get started</Text>
          <form onSubmit={handleSignup} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={textColor}>First Name</FormLabel>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Last Name</FormLabel>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Password</FormLabel>
                <InputGroup>
                  <Input
                    id="password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    id="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      icon={
                        showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                      }
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button
                type="submit"
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                width="full"
                isLoading={loading}
              >
                Create account
              </Button>
            </VStack>
          </form>
          <Text color={textColor}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Sign in
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default Signup;
